'use client';

import React, { useContext, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Box } from '@mantine/core';
import { useResponsive } from '@/hooks/use-responsive';
import { useRender } from '@/hooks/use-render';
import { NavigationContext } from '@/context/Navigation';
import type { MarkdownContent } from '@/app/page';
import { HomeFeatureSection } from '@/components/landing/HomeFeatureSection';

// Dynamic imports for optimization
const Hero = dynamic(() => import('@/components/landing/Hero'));
const DocumentReviews = dynamic(() => import('@/components/landing/DocumentReviews'));
const ByLawsSection = dynamic(() => import('@/components/landing/ByLawsSection'));
const SiteHeader = dynamic(() => import('@/components/SiteHeader'));
const SiteFooter = dynamic(() => import('@/components/SiteFooter'));
const HelpChangeSection = dynamic(() => import('@/components/HelpChangeSection'), { ssr: false });
const MoreFAQSection = dynamic(() => import('@/components/MoreFAQSection'), { ssr: false });
const Clients = dynamic(() => import('@/components/Clients'), { ssr: false });
const FeedbackSection = dynamic(() => import('@/components/FeedbackSection'), { ssr: false });
const HomeCalloutSection = dynamic(() => import('@/components/HomeCalloutSection'));
const FeatureSection = dynamic(() => import('@/components/landing/FeatureSection'));
const TestimonialsSection = dynamic(() => import('@/components/landing/GoogleReview'));
const FAQSection = dynamic(() => import('@/components/FAQSection'), { ssr: false });

interface ClientHomeProps {
  rootMarkdownFiles: MarkdownContent[];
  contentTextMarkdownFiles: MarkdownContent[];
}

function ClientHome({ rootMarkdownFiles, contentTextMarkdownFiles }: ClientHomeProps) {
  const render = useRender({ smoothing: true });
  const { responsive } = useResponsive();
  const navigationContext = useContext(NavigationContext);

  // Find content for each section
  const heroContent = contentTextMarkdownFiles.find(file => file.slug === 'hero-section');
  const documentReviewsContent = contentTextMarkdownFiles.find(file => file.slug === 'document-reviews');
  const homeCalloutContent = contentTextMarkdownFiles.find(file => file.slug === 'home-callout');
  const featureSectionContent = contentTextMarkdownFiles.find(file => file.slug === 'feature-section');
  const byLawsContent = contentTextMarkdownFiles.find(file => file.slug === 'by-laws');
  const homeFeatureSectionContent = contentTextMarkdownFiles.find(file => file.slug === 'home-feature-section');
  const faqContent = contentTextMarkdownFiles.find(file => file.slug === 'faq');
  const feedbackSectionContent = contentTextMarkdownFiles.find(file => file.slug === 'feedback-section');

  // // Console log content for each section
  // useEffect(() => {
  //   console.log('Hero Section Content:', heroContent);
  //   console.log('Document Reviews Content:', documentReviewsContent);
  //   console.log('Home Callout Content:', homeCalloutContent);
  //   console.log('Feature Section Content:', featureSectionContent);
  //   console.log('By Laws Content:', byLawsContent);
  //   console.log('Home Feature Section Content:', homeFeatureSectionContent);
  //   console.log('FAQ Content:', faqContent);
  //   console.log('Feedback Section Content:', feedbackSectionContent);
  // }, [
  //   heroContent,
  //   documentReviewsContent,
  //   homeCalloutContent,
  //   featureSectionContent,
  //   byLawsContent,
  //   homeFeatureSectionContent,
  //   faqContent,
  //   feedbackSectionContent
  // ]);

  useEffect(() => {
    navigationContext.hideLoadingScreen();
  }, [navigationContext]);

  return render(
    <SiteHeader scheme="light" />,
    <main>
      <Hero markdownContent={heroContent} />
      <DocumentReviews markdownContent={documentReviewsContent} />
      <HomeCalloutSection markdownContent={homeCalloutContent} />
      <FeatureSection markdownContent={featureSectionContent} />
      <ByLawsSection
        markdownContent={byLawsContent}
        py={responsive({
          xs: 40,
          lg: 64,
        })}
      />
      <HomeFeatureSection 
        sectionId="home-feature-section" 
        markdownContent={homeFeatureSectionContent}
      />
      <Clients />
      <TestimonialsSection />
      <FAQSection markdownContent={faqContent} />
      <MoreFAQSection />
      <FeedbackSection markdownContent={feedbackSectionContent} />
      <HelpChangeSection />
      <SiteFooter />
    </main>
  );
}

// Export the component as default
export default ClientHome; 